import { useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { create } from 'zustand'
import { Icon } from './Icon'
import { useDialogStack, useModalDialog } from './dialog'

/**
 * Imperative overlays with the same call shape as Ionic's controllers:
 *
 *   const toast = await toastController.create({ message, duration, color })
 *   await toast.present()
 *
 *   const alert = await alertController.create({ header, message, inputs, buttons })
 *   await alert.present()
 *   const { data, role } = await alert.onDidDismiss()
 *
 * Mount <OverlayHost /> once at the app root. Alerts and action sheets are
 * native modal <dialog>s; toasts render inside the top-most open dialog (or
 * the body) so they stay visible and clickable above a modal.
 */

/* ---------------------------------------------------------------------------
   Types
   ------------------------------------------------------------------------ */

export interface OverlayButton {
  text: string
  role?: 'cancel' | 'destructive' | 'confirm' | string
  icon?: string
  cssClass?: string | string[]
  data?: unknown
  /** Return `false` to keep the overlay open. */
  handler?: (value?: any) => boolean | void | Promise<boolean | void>
}

export interface AlertInput {
  name?: string
  type?: 'text' | 'number' | 'email' | 'password' | 'tel' | 'url' | 'date' | 'textarea' | 'radio' | 'checkbox'
  label?: string
  placeholder?: string
  value?: any
  checked?: boolean
  disabled?: boolean
  min?: number | string
  max?: number | string
  attributes?: Record<string, any>
  id?: string
}

export interface AlertOptions {
  header?: string
  subHeader?: string
  message?: string
  inputs?: AlertInput[]
  buttons?: Array<string | OverlayButton>
  backdropDismiss?: boolean
  cssClass?: string | string[]
}

export interface ToastOptions {
  message?: string
  header?: string
  duration?: number
  color?: string
  position?: 'top' | 'bottom' | 'middle'
  icon?: string
  buttons?: Array<string | OverlayButton>
  cssClass?: string | string[]
}

export interface ActionSheetOptions {
  header?: string
  subHeader?: string
  buttons: Array<string | OverlayButton>
  backdropDismiss?: boolean
  cssClass?: string | string[]
}

export interface OverlayEventDetail<T = any> {
  data?: T
  role?: string
}

export interface OverlayHandle<T = any> {
  present: () => Promise<void>
  dismiss: (data?: any, role?: string) => Promise<boolean>
  onDidDismiss: () => Promise<OverlayEventDetail<T>>
  onWillDismiss: () => Promise<OverlayEventDetail<T>>
}

type Kind = 'alert' | 'toast' | 'actionSheet'

interface Entry {
  id: number
  kind: Kind
  options: any
  close: (data?: any, role?: string) => void
}

/* ---------------------------------------------------------------------------
   Store
   ------------------------------------------------------------------------ */

const useOverlays = create<{ entries: Entry[] }>(() => ({ entries: [] }))

let nextId = 1

const normalizeButtons = (buttons: Array<string | OverlayButton> | undefined): OverlayButton[] =>
  (buttons || []).map((b) => (typeof b === 'string' ? { text: b } : b))

function createHandle<T> (kind: Kind, options: any): OverlayHandle<T> {
  const id = nextId++
  let resolveDismiss!: (d: OverlayEventDetail<T>) => void
  const dismissed = new Promise<OverlayEventDetail<T>>((r) => { resolveDismiss = r })
  let open = false
  let done = false
  let timer: ReturnType<typeof setTimeout> | null = null

  const close = (data?: any, role?: string) => {
    if (done) return
    done = true
    open = false
    if (timer) clearTimeout(timer)
    useOverlays.setState((s) => ({ entries: s.entries.filter((e) => e.id !== id) }))
    resolveDismiss({ data, role })
  }

  return {
    present: async () => {
      if (open || done) return
      open = true
      useOverlays.setState((s) => ({ entries: [...s.entries, { id, kind, options, close }] }))
      if (kind === 'toast' && options.duration && options.duration > 0) {
        timer = setTimeout(() => close(undefined, 'timeout'), options.duration)
      }
    },
    dismiss: async (data?: any, role?: string) => {
      if (done) return false
      close(data, role)
      return true
    },
    onDidDismiss: () => dismissed,
    onWillDismiss: () => dismissed
  }
}

export const alertController = {
  create: async (options: AlertOptions) => createHandle('alert', options),
  /** Dismiss the top-most alert, if any. */
  dismiss: async (data?: any, role?: string) => {
    const top = [...useOverlays.getState().entries].reverse().find((e) => e.kind === 'alert')
    top?.close(data, role)
    return Boolean(top)
  },
  getTop: async () => [...useOverlays.getState().entries].reverse().find((e) => e.kind === 'alert')
}

export const toastController = {
  create: async (options: ToastOptions) => createHandle('toast', options),
  dismiss: async () => {
    const top = [...useOverlays.getState().entries].reverse().find((e) => e.kind === 'toast')
    top?.close(undefined, 'dismiss')
    return Boolean(top)
  }
}

export const actionSheetController = {
  create: async (options: ActionSheetOptions) => createHandle('actionSheet', options)
}

/* ---------------------------------------------------------------------------
   Convenience helpers for new code
   ------------------------------------------------------------------------ */

export async function showToast (message: string, color: string = 'primary', duration = 2500, position: ToastOptions['position'] = 'bottom') {
  const t = await toastController.create({ message, color, duration, position })
  await t.present()
}

/** Resolves true when the confirm button is pressed. */
export async function confirmAlert ({
  header,
  message,
  confirmText = 'OK',
  cancelText = 'Cancel',
  destructive = false
}: { header?: string; message?: string; confirmText?: string; cancelText?: string; destructive?: boolean }): Promise<boolean> {
  const a = await alertController.create({
    header,
    message,
    buttons: [
      { text: cancelText, role: 'cancel' },
      { text: confirmText, role: destructive ? 'destructive' : 'confirm' }
    ]
  })
  await a.present()
  const { role } = await a.onDidDismiss()
  return role === 'confirm' || role === 'destructive'
}

/* ---------------------------------------------------------------------------
   Rendering
   ------------------------------------------------------------------------ */

const ALLOWED_TAGS = new Set(['B', 'STRONG', 'I', 'EM', 'BR', 'P', 'UL', 'OL', 'LI', 'SPAN', 'SMALL', 'A', 'DIV'])

/** Ionic alert messages accept light HTML; keep the formatting, drop anything active. */
function sanitize (html: string): string {
  // Ionic set alert messages as innerHTML, so plain newlines collapse to spaces.
  if (!/[<&]/.test(html)) return html
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html')
  const walk = (node: Element) => {
    for (const child of Array.from(node.children)) {
      if (!ALLOWED_TAGS.has(child.tagName)) {
        child.replaceWith(...Array.from(child.childNodes))
        continue
      }
      for (const attr of Array.from(child.attributes)) {
        const keep = child.tagName === 'A' && attr.name === 'href' && /^(https?:|mailto:|tel:)/i.test(attr.value)
        if (!keep) child.removeAttribute(attr.name)
      }
      if (child.tagName === 'A') {
        child.setAttribute('target', '_blank')
        child.setAttribute('rel', 'noopener noreferrer')
      }
      walk(child)
    }
  }
  const root = doc.body.firstElementChild as Element
  walk(root)
  return root.innerHTML
}

const roleClass = (role?: string) =>
  role === 'cancel' ? 'alert__button--cancel' : role === 'destructive' ? 'alert__button--destructive' : ''

function AlertView ({ entry }: { entry: Entry }) {
  const options = entry.options as AlertOptions
  const inputs = options.inputs || []
  const buttons = normalizeButtons(options.buttons)
  const radio = inputs.length > 0 && inputs.every((i) => i.type === 'radio')
  const checkbox = inputs.length > 0 && inputs.every((i) => i.type === 'checkbox')

  const [values, setValues] = useState<Record<string, any>>(() => {
    const v: Record<string, any> = {}
    inputs.forEach((input, idx) => {
      v[input.name ?? String(idx)] = input.value ?? ''
    })
    return v
  })
  // Selected value per radio group (keyed by input name). Mixed alerts (e.g. an
  // amount field plus "Cash / E-transfer" radios) return the selected value
  // under the group's name; Ionic returned the last radio's value instead.
  const radioGroup = (input: AlertInput) => input.name ?? '__radio'
  const [radioByName, setRadioByName] = useState<Record<string, any>>(() => {
    const out: Record<string, any> = {}
    inputs.forEach((i) => {
      if (i.type === 'radio' && i.checked) out[radioGroup(i)] = i.value
    })
    return out
  })
  const [checked, setChecked] = useState<Set<number>>(
    () => new Set(inputs.map((i, idx) => (i.type === 'checkbox' && i.checked ? idx : -1)).filter((i) => i >= 0))
  )
  const firstInput = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null)
  const dialogRef = useRef<HTMLDialogElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)

  const collect = () => {
    if (radio) return radioByName[radioGroup(inputs[0])]
    if (checkbox) return inputs.filter((_, idx) => checked.has(idx)).map((i) => i.value)
    if (inputs.length) {
      const out: Record<string, any> = { ...values }
      inputs.forEach((i) => {
        if (i.type === 'radio') out[radioGroup(i)] = radioByName[radioGroup(i)]
      })
      return out
    }
    return undefined
  }

  const press = async (button: OverlayButton) => {
    const value = collect()
    if (button.handler) {
      const result = await button.handler(value)
      if (result === false) return
    }
    entry.close(inputs.length ? { values: value } : button.data, button.role)
  }

  const cancel = () => {
    const cancelButton = buttons.find((b) => b.role === 'cancel')
    if (cancelButton?.handler) void cancelButton.handler(collect())
    entry.close(undefined, 'backdrop')
  }

  useModalDialog(dialogRef, {
    onCancel: () => { if (options.backdropDismiss !== false) cancel() },
    initialFocus: () => firstInput.current ?? panelRef.current
  })

  const primary = [...buttons].reverse().find((b) => b.role !== 'cancel')
  const stacked = buttons.length > 2 || buttons.some((b) => b.text.length > 14)

  const titleId = `alert-${entry.id}-title`
  const messageId = `alert-${entry.id}-message`
  return (
    <dialog
      ref={dialogRef}
      className="overlay-backdrop overlay-backdrop--alert"
      role="alertdialog"
      aria-labelledby={options.header ? titleId : undefined}
      aria-label={options.header ? undefined : options.subHeader || 'Alert'}
      aria-describedby={options.message ? messageId : undefined}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && options.backdropDismiss !== false) cancel()
      }}
    >
      <div ref={panelRef} className="alert" tabIndex={-1}>
        <div className="alert__body">
          {options.header ? <h2 className="alert__title" id={titleId}>{options.header}</h2> : null}
          {options.subHeader ? <p className="alert__subtitle">{options.subHeader}</p> : null}
          {options.message ? <div className="alert__message" id={messageId} dangerouslySetInnerHTML={{ __html: sanitize(options.message) }} /> : null}
          {inputs.length ? (
            <form
              className="alert__inputs"
              onSubmit={(e) => {
                e.preventDefault()
                if (primary) void press(primary)
              }}
            >
              {inputs.map((input, idx) => {
                const key = input.name ?? String(idx)
                if (input.type === 'radio') {
                  return (
                    <label key={idx} className="check">
                      <input
                        type="radio"
                        name={`alert-${entry.id}-${radioGroup(input)}`}
                        checked={radioByName[radioGroup(input)] === input.value}
                        disabled={input.disabled}
                        onChange={() => setRadioByName((prev) => ({ ...prev, [radioGroup(input)]: input.value }))}
                      />
                      <span>{input.label}</span>
                    </label>
                  )
                }
                if (input.type === 'checkbox') {
                  return (
                    <label key={idx} className="check">
                      <input
                        type="checkbox"
                        checked={checked.has(idx)}
                        disabled={input.disabled}
                        onChange={(e) =>
                          setChecked((prev) => {
                            const next = new Set(prev)
                            if (e.target.checked) next.add(idx)
                            else next.delete(idx)
                            return next
                          })
                        }
                      />
                      <span>{input.label}</span>
                    </label>
                  )
                }
                if (input.type === 'textarea') {
                  return (
                    <textarea
                      key={idx}
                      ref={idx === 0 ? (el) => { firstInput.current = el } : undefined}
                      className="field__control"
                      placeholder={input.placeholder}
                      value={values[key] ?? ''}
                      disabled={input.disabled}
                      {...(input.attributes || {})}
                      onChange={(e) => setValues((v) => ({ ...v, [key]: e.target.value }))}
                    />
                  )
                }
                return (
                  <input
                    key={idx}
                    ref={idx === 0 ? (el) => { firstInput.current = el } : undefined}
                    className="field__control"
                    type={input.type || 'text'}
                    placeholder={input.placeholder}
                    value={values[key] ?? ''}
                    min={input.min}
                    max={input.max}
                    disabled={input.disabled}
                    aria-label={input.label || input.placeholder || input.name}
                    {...(input.attributes || {})}
                    onChange={(e) => setValues((v) => ({ ...v, [key]: e.target.value }))}
                  />
                )
              })}
              <button type="submit" hidden />
            </form>
          ) : null}
        </div>
        {buttons.length ? (
          <div className={`alert__buttons${stacked ? ' alert__buttons--stacked' : ''}`}>
            {buttons.map((b, idx) => (
              <button
                key={idx}
                type="button"
                className={`alert__button ${roleClass(b.role)} ${b === primary ? 'alert__button--primary-action' : ''}`}
                onClick={() => void press(b)}
              >
                {b.text}
              </button>
            ))}
          </div>
        ) : null}
      </div>
    </dialog>
  )
}

function ActionSheetView ({ entry }: { entry: Entry }) {
  const options = entry.options as ActionSheetOptions
  const buttons = normalizeButtons(options.buttons)
  const cancelButton = buttons.find((b) => b.role === 'cancel')
  const others = buttons.filter((b) => b !== cancelButton)

  const press = async (button: OverlayButton) => {
    if (button.handler) {
      const result = await button.handler()
      if (result === false) return
    }
    entry.close(button.data, button.role)
  }

  const cancel = () => {
    if (cancelButton) void press(cancelButton)
    else entry.close(undefined, 'backdrop')
  }

  const dialogRef = useRef<HTMLDialogElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  useModalDialog(dialogRef, {
    onCancel: () => { if (options.backdropDismiss !== false) cancel() },
    initialFocus: () => panelRef.current
  })

  return (
    <dialog
      ref={dialogRef}
      className="overlay-backdrop overlay-backdrop--sheet-actions"
      aria-label={options.header || 'Actions'}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && options.backdropDismiss !== false) cancel()
      }}
    >
      <div ref={panelRef} className="action-sheet" tabIndex={-1}>
        <div className="action-sheet__group">
          {options.header || options.subHeader ? (
            <div className="action-sheet__title">
              {options.header}
              {options.subHeader ? <div>{options.subHeader}</div> : null}
            </div>
          ) : null}
          {others.map((b, idx) => (
            <button
              key={idx}
              type="button"
              className={`action-sheet__button ${b.role === 'destructive' ? 'action-sheet__button--destructive' : ''}`}
              onClick={() => void press(b)}
            >
              {b.icon ? <Icon icon={b.icon} /> : null}
              {b.text}
            </button>
          ))}
        </div>
        {cancelButton ? (
          <div className="action-sheet__group">
            <button type="button" className="action-sheet__button action-sheet__button--cancel" onClick={() => void press(cancelButton)}>
              {cancelButton.text}
            </button>
          </div>
        ) : null}
      </div>
    </dialog>
  )
}

function ToastView ({ entry }: { entry: Entry }) {
  const options = entry.options as ToastOptions
  const buttons = normalizeButtons(options.buttons)
  return (
    <div className={`toast toast--${options.color || 'primary'}`} role={options.color === 'danger' ? 'alert' : 'status'}>
      {options.icon ? <Icon icon={options.icon} /> : null}
      <div className="toast__message">
        {options.header ? <div className="toast__header">{options.header}</div> : null}
        {options.message}
      </div>
      {buttons.map((b, idx) => (
        <button
          key={idx}
          type="button"
          className="toast__button"
          onClick={async () => {
            if (b.handler) {
              const result = await b.handler()
              if (result === false) return
            }
            entry.close(b.data, b.role)
          }}
        >
          {b.text}
        </button>
      ))}
    </div>
  )
}

export function OverlayHost () {
  const entries = useOverlays((s) => s.entries)
  // Inside an open modal everything else is inert, so toasts portal into the
  // top-most dialog; there they paint above its panel and their buttons work.
  const topDialog = useDialogStack((s) => s.stack[s.stack.length - 1])
  const toasts = entries.filter((e) => e.kind === 'toast')
  const byPosition = (pos: string) => toasts.filter((t) => (t.options.position || 'bottom') === pos)
  const toastStacks = (['top', 'middle', 'bottom'] as const).map((pos) => {
    const list = byPosition(pos)
    if (!list.length) return null
    return (
      <div key={pos} className={`toast-stack toast-stack--${pos}`} aria-live="polite">
        {list.map((t) => <ToastView key={t.id} entry={t} />)}
      </div>
    )
  })
  // Portaled to <body> so nothing in the app shell (inert / aria-hidden
  // wrappers, transforms) can affect them.
  return createPortal(
    <>
      {entries
        .filter((e) => e.kind !== 'toast')
        .map((e) => (e.kind === 'alert' ? <AlertView key={e.id} entry={e} /> : <ActionSheetView key={e.id} entry={e} />))}
      {topDialog ? createPortal(toastStacks, topDialog) : toastStacks}
    </>,
    document.body
  )
}
